import { z } from 'zod';

const isProduction = import.meta.env.PROD;

const stringBool = z
  .union([z.string(), z.boolean()])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

const baseSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .url('VITE_API_BASE_URL must be a valid URL')
    .default('http://localhost:5000/api/v1'),
  VITE_RAZORPAY_KEY_ID: z
    .string()
    .min(1, 'VITE_RAZORPAY_KEY_ID is required')
    .default('rzp_test_local_dev_placeholder'),
  VITE_CLOUDINARY_CLOUD_NAME: z
    .string()
    .min(1, 'VITE_CLOUDINARY_CLOUD_NAME is required')
    .default('quickxkart'),
  VITE_SENTRY_DSN: z.string().url().optional().or(z.literal('')),
  // Google Sign-In Client ID — public by design (ships in the bundle), so a
  // committed default keeps the button working without a dashboard env var.
  VITE_GOOGLE_CLIENT_ID: z
    .string()
    .default('46829730059-rq20ltf1a9f4e10gk6ser7k5519t7up1.apps.googleusercontent.com'),
  VITE_ENABLE_3D: stringBool.default(true),
  VITE_ENABLE_ANALYTICS: stringBool.default(false),
  VITE_ENABLE_MSW: stringBool.default(false),
});

const productionSchema = baseSchema.extend({
  VITE_API_BASE_URL: z
    .string()
    .url('VITE_API_BASE_URL must be a valid URL')
    .refine(
      (url) => !url.includes('localhost') && !url.includes('127.0.0.1'),
      'Production VITE_API_BASE_URL must not point to localhost'
    ),
  VITE_RAZORPAY_KEY_ID: z
    .string()
    .min(1)
    .refine(
      (key) => key.startsWith('rzp_'),
      'VITE_RAZORPAY_KEY_ID must start with rzp_ (live or test)'
    ),
});

const isMswMode = import.meta.env.VITE_ENABLE_MSW === 'true';
const schema = isProduction && !isMswMode ? productionSchema : baseSchema;

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
    .join('\n');
  // eslint-disable-next-line no-console
  console.error(`\n❌ Invalid environment variables:\n${issues}\n`);
  throw new Error('Environment validation failed. See console for details.');
}

export const env = parsed.data;

export type Env = z.infer<typeof schema>;
